import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, format } from "date-fns";
import { ArrowLeft, Send, User as UserIcon, Shield } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AttachmentList,
  AttachmentUploader,
  type TicketAttachment,
} from "@/components/tickets/attachment-uploader";

export const Route = createFileRoute("/_authenticated/_account/tickets/$ticketId")({
  component: TicketDetail,
});

type Reply = {
  id: string;
  ticket_id: string;
  author_id: string | null;
  body: string;
  is_internal: boolean;
  created_at: string;
  attachments: TicketAttachment[] | null;
};

type Ticket = {
  id: string;
  user_id: string;
  subject: string;
  body: string;
  status: string;
  priority: string;
  category: string | null;
  created_at: string;
  updated_at: string;
  attachments: TicketAttachment[] | null;
};

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  open: "default",
  pending: "secondary",
  on_hold: "outline",
  solved: "outline",
  closed: "outline",
};

const priorityClass: Record<string, string> = {
  low: "text-muted-foreground border-border",
  normal: "text-foreground border-border",
  high: "text-amber-500 border-amber-500/40",
  urgent: "text-red-500 border-red-500/40",
};

function TicketDetail() {
  const { ticketId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<TicketAttachment[]>([]);

  const ticketQ = useQuery({
    queryKey: ["ticket", ticketId],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("id, user_id, subject, body, status, priority, category, created_at, updated_at, attachments")
        .eq("id", ticketId)
        .maybeSingle();
      if (error) throw error;
      return data as Ticket | null;
    },
  });

  const repliesQ = useQuery({
    queryKey: ["ticket_replies", ticketId],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ticket_replies")
        .select("id, ticket_id, author_id, body, is_internal, created_at, attachments")
        .eq("ticket_id", ticketId)
        .eq("is_internal", false)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Reply[];
    },
  });

  const sendReply = useMutation({
    mutationFn: async (body: string) => {
      const { error } = await supabase.from("ticket_replies").insert({
        ticket_id: ticketId,
        author_id: user!.id,
        body,
        is_internal: false,
        attachments: replyAttachments,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setReply("");
      setReplyAttachments([]);
      toast.success("Reply sent");
      qc.invalidateQueries({ queryKey: ["ticket_replies", ticketId] });
      qc.invalidateQueries({ queryKey: ["my_tickets"] });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to send reply"),
  });

  const ticket = ticketQ.data;
  const replies = repliesQ.data ?? [];
  const isClosed = ticket?.status === "closed" || ticket?.status === "solved";

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/tickets" })} className="-ml-2">
          <ArrowLeft className="h-4 w-4" /> Back to tickets
        </Button>
      </div>

      {ticketQ.isLoading ? (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading ticket…
        </div>
      ) : !ticket ? (
        <div className="rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
          <h2 className="text-sm font-medium">Ticket not found</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            It may have been removed or you don't have access.
          </p>
          <Button asChild className="mt-4">
            <Link to="/tickets">Back to my tickets</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-3 rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <h1 className="text-xl font-semibold">{ticket.subject}</h1>
                <p className="text-xs text-muted-foreground">
                  Opened {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                  {ticket.category ? ` · ${ticket.category}` : ""}
                </p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <Badge variant="outline" className={priorityClass[ticket.priority] ?? ""}>
                  {ticket.priority}
                </Badge>
                <Badge variant={statusVariant[ticket.status] ?? "secondary"} className="capitalize">
                  {ticket.status.replace("_", " ")}
                </Badge>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">Conversation</h2>
            <Message
              author="You"
              isMine
              body={ticket.body}
              createdAt={ticket.created_at}
              label="Original message"
              attachments={ticket.attachments ?? []}
            />
            {replies.map((r) => {
              const mine = r.author_id === user?.id;
              return (
                <Message
                  key={r.id}
                  author={mine ? "You" : "Support"}
                  isMine={mine}
                  body={r.body}
                  createdAt={r.created_at}
                  attachments={r.attachments ?? []}
                />
              );
            })}
            {repliesQ.isLoading && (
              <div className="text-xs text-muted-foreground">Loading replies…</div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-medium">{replies.length ? "Add a reply" : "Send the first reply"}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {isClosed
                ? "This ticket is closed. Replying will not reopen it automatically."
                : "Our team will be notified when you reply."}
            </p>
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Type your message…"
              rows={5}
              className="mt-3"
              disabled={sendReply.isPending}
            />
            {user && (
              <div className="mt-3">
                <AttachmentUploader
                  userId={user.id}
                  prefix="replies"
                  value={replyAttachments}
                  onChange={setReplyAttachments}
                  disabled={sendReply.isPending}
                />
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <Button
                onClick={() => sendReply.mutate(reply.trim())}
                disabled={
                  sendReply.isPending ||
                  (!reply.trim() && replyAttachments.length === 0)
                }
              >
                <Send className="h-4 w-4" />
                {sendReply.isPending ? "Sending…" : "Send reply"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Message({
  author,
  isMine,
  body,
  createdAt,
  label,
  attachments = [],
}: {
  author: string;
  isMine: boolean;
  body: string;
  createdAt: string;
  label?: string;
  attachments?: TicketAttachment[];
}) {
  return (
    <div className={`flex gap-3 ${isMine ? "flex-row-reverse" : ""}`}>
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
          isMine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
        }`}
      >
        {isMine ? <UserIcon className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
      </div>
      <div className={`min-w-0 flex-1 ${isMine ? "items-end text-right" : ""}`}>
        <div className={`flex items-center gap-2 text-xs text-muted-foreground ${isMine ? "justify-end" : ""}`}>
          <span className="font-medium text-foreground">{author}</span>
          {label && <span>· {label}</span>}
          <span>· {format(new Date(createdAt), "MMM d, yyyy 'at' h:mm a")}</span>
        </div>
        {body && (
          <div
            className={`mt-1 inline-block max-w-full whitespace-pre-wrap rounded-lg border px-4 py-3 text-sm text-left ${
              isMine
                ? "border-primary/20 bg-primary/10"
                : "border-border bg-card"
            }`}
          >
            {body}
          </div>
        )}
        {attachments.length > 0 && (
          <div className={isMine ? "flex justify-end" : ""}>
            <AttachmentList items={attachments} />
          </div>
        )}
      </div>
    </div>
  );
}
